import { format, parseISO } from 'date-fns';
import React from 'react';
import {
  Bar,
  CartesianGrid,
  Line,
  ComposedChart as RechartsComposedChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

export interface ChartDataPoint {
  date: string;
  [key: string]: string | number | unknown;
}

export interface ChartSeries {
  dataKey: string;
  name: string;
  type: 'line' | 'bar';
  color: string;
}

// Tooltip content type for recharts
export type TooltipContentType = (props: TooltipProps<ValueType, NameType>) => React.ReactNode;

interface ComposedChartProps {
  data: ChartDataPoint[];
  series: ChartSeries[];
  height?: number;
  yAxisFormatter?: (value: number) => string;
  customTooltip?: TooltipContentType;
}

/**
 * Check if a series should use the right Y axis (cumulative data)
 */
const isCumulativeSeries = (dataKey: string): boolean => {
  return dataKey.toLowerCase().includes('cumulative');
};

/**
 * Composed chart component supporting both line and bar charts
 * Uses real data from Analytics API
 * Supports dual Y-axis: left for regular data, right for cumulative data
 */
const ComposedChart: React.FC<ComposedChartProps> = ({
  data,
  series,
  height = 232,
  yAxisFormatter = value => value.toLocaleString(),
  customTooltip,
}) => {
  // Check if we need dual Y-axis (has cumulative series)
  const hasCumulativeSeries = series.some(s => isCumulativeSeries(s.dataKey));
  const hasDualAxis = series.length >= 2 && hasCumulativeSeries;

  // Format date for display
  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'MM/dd');
    } catch {
      return dateStr;
    }
  };

  // Custom tooltip
  const CustomTooltip: TooltipContentType = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    return (
      <div className="bg-solid-white-card border border-border-light-line-1 rounded-lg p-3 shadow-lg">
        <p className="text-sm font-semibold text-text-black mb-2">{formatDate(label)}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-text-main-body">{entry.name}:</span>
            <span className="font-semibold text-text-black">{yAxisFormatter(entry.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsComposedChart
        data={data}
        margin={{ top: 5, right: hasDualAxis ? 0 : 10, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light-line-1)" />
        <XAxis
          dataKey="date"
          tickFormatter={formatDate}
          stroke="var(--text-main-body)"
          style={{ fontSize: '12px' }}
          tickLine={false}
        />
        {/* Left Y-Axis for regular data */}
        <YAxis
          yAxisId="left"
          tickFormatter={yAxisFormatter}
          stroke="var(--text-main-body)"
          style={{ fontSize: '12px' }}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
          width={45}
        />
        {/* Right Y-Axis for cumulative data (only when dual axis is needed) */}
        {hasDualAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={yAxisFormatter}
            stroke="var(--text-main-body)"
            style={{ fontSize: '12px' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={45}
          />
        )}
        <Tooltip content={customTooltip || CustomTooltip} />
        {series.map(s => {
          const useRightAxis = hasDualAxis && isCumulativeSeries(s.dataKey);
          const yAxisId = useRightAxis ? 'right' : 'left';

          if (s.type === 'line') {
            return (
              <Line
                key={s.dataKey}
                yAxisId={yAxisId}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: s.color }}
              />
            );
          } else {
            return (
              <Bar
                key={s.dataKey}
                yAxisId={yAxisId}
                dataKey={s.dataKey}
                name={s.name}
                fill={s.color}
                radius={[0, 0, 0, 0]}
              />
            );
          }
        })}
      </RechartsComposedChart>
    </ResponsiveContainer>
  );
};

export default ComposedChart;
